/**
 * ChatMessageBubble — single message in the simulador transcript.
 *
 * Patient bubbles align left with neutral background; user bubbles align
 * right with the primary accent. `data-seq` is set so the report screen can
 * scroll-to-message via key-moments / annotations.
 */
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { bubbleMotion } from "../lib/chatMotion";
import type { SimulatorMessage } from "../types";

export interface ChatMessageBubbleProps {
  message: SimulatorMessage;
}

function formatTime(iso?: string): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function ChatMessageBubble({ message }: ChatMessageBubbleProps) {
  const isUser = message.role === "user";
  const isPatient = message.role === "patient";
  const isHint = message.role === "hint";
  if (!isUser && !isPatient && !isHint) return null; // hide system/classifier

  if (isHint) {
    return (
      <motion.div
        data-seq={message.seq}
        className="flex w-full justify-center"
        variants={bubbleMotion.hint}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <div className="max-w-[90%] rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-200">
          <span className="mr-2 rounded-md bg-amber-500/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-100">
            Coach
          </span>
          <span className="whitespace-pre-wrap break-words">{message.content}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      data-seq={message.seq}
      className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}
      variants={isUser ? bubbleMotion.user : bubbleMotion.patient}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
          isUser
            ? "bg-[#247eaf] text-slate-50 rounded-br-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            : "bg-secondary text-foreground rounded-bl-sm",
        )}
      >
        <p className="whitespace-pre-wrap break-words">{message.content}</p>
        {message.created_at ? (
          <span
            className={cn(
              "block text-[10px] mt-1 opacity-70",
              isUser ? "text-right" : "text-left",
            )}
          >
            {formatTime(message.created_at)}
          </span>
        ) : null}
      </div>
    </motion.div>
  );
}
